/**
 * RouteTracker — emits ONE "Page Viewed" per real route change.
 *
 * Mixpanel's built-in track_pageview only fires on hard page loads, so in a
 * react-router SPA every client-side navigation was invisible. This component
 * lives inside <Router> and watches location, so /onboard -> /plans -> /user
 * each produce exactly one pageview with a stable page_name.
 */

import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { track, EVENTS } from "../utils/analytics";

/** Map a pathname to a stable, low-cardinality page name for reporting. */
export function pageNameFromPath(pathname) {
  const p = (pathname || "/").toLowerCase().replace(/\/+$/, "") || "/";

  const exact = {
    "/": "home",
    "/about": "about",
    "/science": "science",
    "/beta": "beta_landing",
    "/welcome": "welcome_landing",
    "/main": "main_landing",
    "/concept": "concept_waitlist",
    "/onboarding": "onboarding_v1",
    "/onboard": "onboarding_v2",
    "/user": "app_chat",
    "/connect": "app_connect",
    "/connected": "connected",
    "/coach": "coach_init",
    "/texting": "texting_consent",
    "/privacy-policy": "privacy_policy",
    "/delete-account": "delete_account",
    "/purpose": "purpose_assessment",
    "/life-score": "life_score",
    "/wheel-of-life": "wheel_of_life",
    "/plans": "plans",
    "/pricing": "pricing",
    "/subscribe": "subscribe",
    "/billing": "billing",
    "/checkout": "checkout",
    "/landing": "sales_landing",
    "/resolutions": "resolutions",
    "/hir": "hir_landing",
    "/hir2": "hir_landing_2",
    "/stride": "stride_landing",
    "/career_planner": "stride_career_planner",
    "/stride_dashboard": "stride_dashboard",
    "/panel": "panel_landing",
    "/panel/about": "panel_about",
    "/admin": "admin",
    "/admin_data": "admin_data",
  };
  if (exact[p]) return exact[p];

  // Parameterised routes collapse to one name so cardinality stays sane.
  if (p.startsWith("/connect/")) return "app_connect_phone";
  if (p.startsWith("/life-score/")) return "life_score";
  if (p.startsWith("/wheel-of-life/")) return "wheel_of_life";
  if (p.startsWith("/groups/")) return "group_coaching_landing";
  if (p.startsWith("/coach/")) return "coach_dashboard";
  if (p.startsWith("/admin/")) return "admin";

  return "unknown";
}

export default function RouteTracker() {
  const location = useLocation();
  const lastTracked = useRef(null);

  useEffect(() => {
    const key = location.pathname + location.search;
    // React 18 StrictMode double-invokes effects in dev, and some components
    // re-render on state change; guard so one navigation = one event.
    if (lastTracked.current === key) return;
    lastTracked.current = key;

    track(EVENTS.PAGE_VIEWED, {
      page: pageNameFromPath(location.pathname),
      path: location.pathname,
      search: location.search || null,
      hash: location.hash || null,
    });
  }, [location.pathname, location.search, location.hash]);

  return null;
}
